import type { FuseResultWithScore } from 'fuse.js';
import { colorSimilarity } from './colorSimilarity';
import { cosineSimilarity } from './cosineSimilarity';
import { getCuratedRank, isPerfectSimilarity } from './curatedSearchResults';
import type { Embedding, ScoreBreakdown, ThemeEmbeddings } from './search.types';

export const SIMILARITY_THRESHOLD = 0.44;
export const SIMILARITY_TIEBREAK_THRESHOLD = SIMILARITY_THRESHOLD * 0.5;
const SIMILARITY_TIE_EPSILON = 1e-9;

/**
 * Difference between two accumulated float scores, with sub-epsilon results
 * collapsed to exactly 0 so callers can treat them as tied.
 *
 * Every rung of the sort that compares accumulated float scores must go through
 * this. Subtracting them directly and testing `!== 0` treats ~1e-16 rounding
 * noise as a meaningful difference, which silently makes every lower-precedence
 * tiebreak unreachable.
 */
const epsilonDiff = (scoreB: number, scoreA: number): number => {
  const diff = scoreB - scoreA;
  return Math.abs(diff) >= SIMILARITY_TIE_EPSILON ? diff : 0;
};

type ScoreAndFilterOptions<T, F = T> = Readonly<{
  termEmbedding: Embedding;
  themes: Map<string, ThemeEmbeddings>;
  embeddingMap: Map<string, Embedding>;
  getThemeId: (item: T) => string;
  /**
   * Id used to look up the Fuse (name-match) score; defaults to `getThemeId`.
   * Set this when the embedding id differs from the real themeId — e.g.
   * per-colorway rows keyed `boldly_tomato` that still share the name `boldly`,
   * so the name match applies to every colorway row, not just the base one.
   */
  getLexicalThemeId?: (item: T) => string;
  fuseResults: ReadonlyArray<FuseResultWithScore<F>>;
  /**
   * The raw query. Only used to look up manual curation
   * (`CURATED_THEMES_BY_SEARCH_TERM`); omit it and curation is inert.
   */
  searchTerm?: string;
  /**
   * The row's colorway, so a curated `themeId+colorwayId` entry can target one
   * colorway. Omit it and only bare-themeId entries (which cover all colorways)
   * can match — the right behavior for callers with no colorway concept.
   */
  getColorwayId?: (item: T) => string | undefined;
  getFuseItemThemeId: (item: F) => string;
  threshold: number;
  getItemCustoms?: (item: T) => ReadonlyArray<Embedding>;
  /**
   * When provided, the per-row score breakdown is written into this map keyed
   * by `getThemeId` (the embeddings-index row, so per-colorway). Debug-only —
   * omit it and no breakdown work is retained.
   */
  collectScores?: Map<string, ScoreBreakdown>;
}>;

export function scoreAndFilterByEmbedding<T, F = T>(
  items: ReadonlyArray<T>,
  {
    termEmbedding,
    themes,
    embeddingMap,
    getThemeId,
    getLexicalThemeId,
    fuseResults,
    getFuseItemThemeId,
    searchTerm,
    getColorwayId,
    threshold,
    getItemCustoms,
    collectScores,
  }: ScoreAndFilterOptions<T, F>,
): ReadonlyArray<T> {
  const lexicalThemeId = getLexicalThemeId ?? getThemeId;
  const white = embeddingMap.get('color: white');
  const beige = embeddingMap.get('color: beige');

  const lexicalScores = new Map(fuseResults.map(r => [getFuseItemThemeId(r.item), 1 - (r.score ?? 1)]));

  const similarityMap = new Map<string, number>();
  const similarityTiebreakMap = new Map<string, number>();
  /**
   * Curated rank per row, resolved once here rather than inside the comparator
   * (which would re-run the term normalization and list scan on every one of
   * O(n log n) comparisons — worst exactly for the large perfect-tie clusters
   * this feature targets).
   *
   * Uncurated rows are stored as `Infinity`, so the comparator subtracts two
   * real numbers and an all-uncurated pair yields a plain 0 rather than the
   * `Infinity - Infinity → NaN` a sort callback must never return.
   *
   * Keyed by `getThemeId` (the per-colorway index row) so a `themeId+colorwayId`
   * entry can rank one colorway differently from its siblings.
   */
  const curatedRankMap = new Map<string, number>();

  for (const item of items) {
    const themeId = getThemeId(item);
    const theme = themes.get(themeId);

    const similarityLexical = lexicalScores.get(lexicalThemeId(item)) ?? 0;
    const scoresConcept = (theme?.concepts ?? []).map(e => cosineSimilarity(termEmbedding, e));
    const similarityConcept = Math.max(0, ...scoresConcept);
    // Sum of above-floor concept scores, used to break ties when overall similarity is equal
    const conceptSumScore = scoresConcept.reduce((sum, s) => sum + Math.max(0, s - SIMILARITY_TIEBREAK_THRESHOLD), 0);
    const itemCustoms = getItemCustoms?.(item) ?? [];
    const allCustoms = [...(theme?.customs ?? []), ...itemCustoms];
    const similarityCustom = Math.max(0, ...allCustoms.map(e => cosineSimilarity(termEmbedding, e)));
    const similarityColor = colorSimilarity(termEmbedding, theme?.colors ?? [], white, beige);

    const similarity = Math.max(
      similarityLexical * 1.01, // A perfect lexical match ranks above all else. *1.01 to tie-break.
      similarityConcept,
      similarityColor,
      similarityCustom,
    );

    similarityMap.set(themeId, similarity);
    similarityTiebreakMap.set(themeId, conceptSumScore);

    // Bare entries cover every colorway of a theme; `+colorwayId` narrows to one.
    const curatedRank = getCuratedRank(searchTerm, { themeId: lexicalThemeId(item), colorwayId: getColorwayId?.(item) });
    curatedRankMap.set(themeId, curatedRank);

    if (collectScores) {
      const weightedLexical = similarityLexical * 1.01;
      // Mirrors the Math.max precedence above, so the reported winner is the
      // factor that actually produced `similarity`. Safe to compare with `===`:
      // Math.max returns one of its arguments unchanged, and `weightedLexical`
      // recomputes the same product from the same operands.
      //
      // An all-zero row reports 'lexical' (0 === 0 matches first), but such rows
      // sit below `threshold` and are filtered out before any tile renders them.
      const winningFactor = weightedLexical === similarity ? 'lexical' : similarityConcept === similarity ? 'concept' : similarityColor === similarity ? 'color' : 'custom';

      // Same per-term arithmetic as `conceptSumScore` above, retained only so the
      // overlay can name which keywords fed the total. Sorted best-cosine-first
      // for readability; `conceptSum` is order-independent, so this cannot change
      // the reported total.
      const conceptNames = theme?.conceptNames ?? [];
      const conceptTerms = scoresConcept
        .map((cosine, i) => ({
          name: conceptNames[i] ?? `concept[${i}]`,
          cosine,
          contribution: Math.max(0, cosine - SIMILARITY_TIEBREAK_THRESHOLD),
        }))
        .sort((x, y) => y.cosine - x.cosine);

      // Whether this row is curated, and whether that curation could actually
      // have moved it. The sort consults curation only among rows tied at a
      // perfect score, so a curated row scoring below that is inert — reporting
      // both answers "I curated this, why didn't it move?" without the reader
      // having to know the gate exists.
      const curation = Number.isFinite(curatedRank) ? { rank: curatedRank, applied: isPerfectSimilarity(similarity) } : undefined;

      collectScores.set(themeId, {
        similarity,
        lexical: similarityLexical,
        weightedLexical,
        concept: similarityConcept,
        color: similarityColor,
        custom: similarityCustom,
        conceptSum: conceptSumScore,
        conceptTerms,
        winningFactor,
        curation,
      });
    }
  }

  const filtered = items.filter(item => (similarityMap.get(getThemeId(item)) ?? 0) >= threshold);
  const sorted = [...filtered].sort((a, b) => {
    // Precedence 1: primary sort on final similarity. Decides outright unless
    // scores are tied — i.e. multiple items resolve their max via the same
    // embedding (e.g. photo-tagged themes all maxing on the shared `custom`
    // cosine).
    const similarityDiff = epsilonDiff(similarityMap.get(getThemeId(b)) ?? 0, similarityMap.get(getThemeId(a)) ?? 0);
    if (similarityDiff !== 0) return similarityDiff;

    // Precedence 2: tiebreak by token-level name match. Fuse is configured with
    // `tokenize: true`, so `lexicalScores` already reflects per-token hits on
    // the template name. A name that literally contains a query word beats one
    // that only matches via embeddings.
    const lexicalDiff = (lexicalScores.get(lexicalThemeId(b)) ?? 0) - (lexicalScores.get(lexicalThemeId(a)) ?? 0);
    if (lexicalDiff !== 0) return lexicalDiff;

    // Precedence 3: manual curation. A popular term like "beach" leaves dozens
    // of themes tied at a clean 1.000 concept match, and the remaining automatic
    // signals can't meaningfully order them — so an editorial list decides the
    // leaders. Deliberately limited to that perfect-tie case: curation resolves
    // a tie the scorer can't, it never overrides a score the scorer *can*
    // distinguish.
    //
    // Ranks come from `curatedRankMap`, resolved per row before the sort;
    // uncurated rows hold `Infinity` and so sort after every curated one while
    // staying tied with each other. Ascending: rank 0 leads.
    //
    // Precedence 1 already proved the two rows equal, so either one's score is
    // the score they share — read off `b` arbitrarily.
    const tiedSimilarity = similarityMap.get(getThemeId(b)) ?? 0;
    if (isPerfectSimilarity(tiedSimilarity)) {
      const rankA = curatedRankMap.get(getThemeId(a)) ?? Infinity;
      const rankB = curatedRankMap.get(getThemeId(b)) ?? Infinity;
      // Two uncurated rows are both Infinity, which `===` treats as equal — so
      // this falls through to the next rung instead of returning a NaN.
      if (rankA !== rankB) return rankA - rankB;
    }

    // Precedence 4: tiebreak by concept breadth. `conceptSum` rewards themes
    // whose style keywords are broadly (if weakly) related to the query,
    // preferring them over themes that got lucky on one factor but are
    // otherwise off-topic.
    //
    // `epsilonDiff` for the same reason as precedence 1: `conceptSum` is an
    // accumulated sum of float cosines, so equivalent themes routinely differ by
    // ~1e-16. (Precedence 2 needs none — `lexicalScores` holds discrete
    // `1 - score` values that do come out exactly equal.)
    return epsilonDiff(similarityTiebreakMap.get(getThemeId(b)) ?? 0, similarityTiebreakMap.get(getThemeId(a)) ?? 0);
  });

  return sorted;
}
